/**
 * Planetary surface weather (Phase 2.2 of the planet gameplay
 * expansion).
 *
 * Two design contracts to keep front-of-mind:
 *
 * 1. Determinism. Both the server simulation and any future
 *    client-side preview have to derive the *same* weather window for
 *    the same `(surfaceKey, hourSinceEpoch)` pair. We pick the kind,
 *    duration, and start-offset with a single seeded RNG below; no
 *    wall-clock RNG, no Math.random fallbacks.
 *
 * 2. PvP parity. Every effect listed in {@link WEATHER_DEF} is
 *    PvP-neutral — uniform across races, never modifies raw damage
 *    output. Engine drag / shield interference apply identically to
 *    every ship inside the sector. The Lava-eruption ambient damage
 *    is small and applies to every ship on the surface, attacker and
 *    defender alike. This keeps repo rule 5 intact even when two
 *    players duel inside an active weather event.
 *
 * The set is intentionally small (one event per biome) — the goal is
 * "the planet feels alive while you're on it", not a deep weather
 * simulation. Add more variants here when you want more flavor; the
 * roster lookup will pick them up without touching the server tick.
 */
import { BiomeId } from '../procedural/surface.js';
export const WEATHER_DEF = {
    clear: { ambientDps: 0, shieldRegenMul: 1.0, engineAccelMul: 1.0, banner: 'CLEAR' },
    lava_eruption: { ambientDps: 1.5, shieldRegenMul: 0.5, engineAccelMul: 1.0, banner: 'LAVA ERUPTION' },
    sandstorm: { ambientDps: 0, shieldRegenMul: 0.7, engineAccelMul: 1.0, banner: 'SANDSTORM' },
    blizzard: { ambientDps: 0, shieldRegenMul: 1.0, engineAccelMul: 0.8, banner: 'BLIZZARD' },
    bioflux_bloom: { ambientDps: 0, shieldRegenMul: 1.2, engineAccelMul: 1.0, banner: 'BIOFLUX BLOOM' },
    meteor_shower: { ambientDps: 0.8, shieldRegenMul: 1.0, engineAccelMul: 1.0, banner: 'METEOR SHOWER' },
};
/** Per-biome event roster the deterministic picker chooses from. */
export const BIOME_WEATHER_ROSTER = {
    [BiomeId.Dirt]: ['sandstorm'],
    [BiomeId.Grass]: ['bioflux_bloom'],
    [BiomeId.Snow]: ['blizzard'],
    [BiomeId.Lava]: ['lava_eruption'],
    [BiomeId.Rock]: ['meteor_shower'],
    [BiomeId.Ice]: ['blizzard'],
};
export const WEATHER_HOUR_MS = 60 * 60 * 1000;
/** A weather event takes up roughly a quarter of every hour. */
export const WEATHER_MIN_DURATION_MS = 45 * 1000;
export const WEATHER_MAX_DURATION_MS = 120 * 1000;
/**
 * 32-bit deterministic hash of `(surfaceKey, hourSinceEpoch)`. Stays
 * inside the JS safe-integer range and produces a uniform-enough
 * stream when fed to {@link weatherRng}. The exact constants don't
 * matter as long as both server and any future client preview agree.
 */
function hashStringHour(surfaceKey, hourSinceEpoch) {
    let h = (hourSinceEpoch | 0) ^ 0x9e3779b1;
    for (let i = 0; i < surfaceKey.length; i++) {
        h = Math.imul(h ^ surfaceKey.charCodeAt(i), 0x85ebca6b);
        h = (h >>> 16) ^ h;
    }
    return h >>> 0;
}
/** Tiny mulberry32 — same shape as `SeededRandom`, inlined here. */
function weatherRng(seed) {
    let state = seed >>> 0;
    return function nextFloat() {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Compute the active weather window for the current hour, given the
 * surface key + biome. Always returns one window per hour — the same
 * pair of `(surfaceKey, hour)` always produces the same kind, start,
 * and duration so every observer in the sector agrees. The window
 * may be `'clear'` if the deterministic roll lands outside the per-
 * biome event chance, in which case ambient effects no-op.
 */
export function pickHourlyWeather(surfaceKey, biome, hourSinceEpoch) {
    const rng = weatherRng(hashStringHour(surfaceKey, hourSinceEpoch));
    // ~40% of hours have an actual weather event; the rest are clear.
    // Tuned so a player visiting a planet for ~15-20 min has a decent
    // chance of seeing one event without weather becoming background
    // noise.
    const eventChance = 0.4;
    const roll = rng();
    const hourStartMs = hourSinceEpoch * WEATHER_HOUR_MS;
    if (roll >= eventChance) {
        return {
            kind: 'clear',
            startsAtMs: hourStartMs,
            endsAtMs: hourStartMs + WEATHER_HOUR_MS,
        };
    }
    const roster = BIOME_WEATHER_ROSTER[biome] ?? [];
    const kind = roster.length > 0
        ? roster[Math.floor(rng() * roster.length)]
        : 'clear';
    const duration = Math.round(WEATHER_MIN_DURATION_MS + rng() * (WEATHER_MAX_DURATION_MS - WEATHER_MIN_DURATION_MS));
    const maxOffset = WEATHER_HOUR_MS - duration;
    const offset = Math.round(rng() * maxOffset);
    return {
        kind,
        startsAtMs: hourStartMs + offset,
        endsAtMs: hourStartMs + offset + duration,
    };
}
/**
 * Return the kind currently active for `nowMs`. If the hourly window
 * doesn't overlap `nowMs`, returns `'clear'`. The server's per-sector
 * tick calls this every tick; the cost is one hash + one rng seed +
 * a couple of multiplications.
 */
export function currentWeather(surfaceKey, biome, nowMs) {
    const hour = Math.floor(nowMs / WEATHER_HOUR_MS);
    const win = pickHourlyWeather(surfaceKey, biome, hour);
    if (win.kind === 'clear')
        return win;
    if (nowMs < win.startsAtMs || nowMs >= win.endsAtMs) {
        return {
            kind: 'clear',
            startsAtMs: hour * WEATHER_HOUR_MS,
            endsAtMs: (hour + 1) * WEATHER_HOUR_MS,
        };
    }
    return win;
}
//# sourceMappingURL=weather.js.map